import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-[#f1f1f1] flex flex-col">
      <Seo
        title="Privacy Policy – CardCombiner"
        description="How CardCombiner handles your data: images are processed in your browser and never uploaded. Read our full privacy policy, cookies, and AdSense disclosures."
        path="/privacy"
      />
      <Navbar />
      <div className="max-w-4xl mx-auto p-4 md:p-8 flex-grow">
        <div className="bg-white p-6 md:p-8 rounded-[16px] md:rounded-[24px] shadow-lg">
          <h1 className="text-3xl md:text-4xl font-bold mb-6 text-[#6B4EE6]">Privacy Policy</h1>
          
          <div className="prose max-w-none text-gray-700">
            <p className="mb-4">Last updated: January 26, 2026</p>
            
            <section className="mb-6">
              <h2 className="text-xl md:text-2xl font-semibold mb-3 text-[#6B4EE6]">
                Introduction
              </h2>
              <p>
                CardCombiner ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you visit our website cardcombiner.com (the "Site").
              </p>
              <p className="mt-2">
                Please read this Privacy Policy carefully. By accessing and using our Site, 
                you acknowledge that you have read, understood, and agree to be bound by the terms of this Privacy Policy.
              </p>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl md:text-2xl font-semibold mb-3 text-[#6B4EE6]">
                Information We Collect
              </h2>
              <p>
                <strong>Personal Data:</strong> We may collect personal identification information that you 
                voluntarily submit to us when you choose to contact us via email or through our contact form.
              </p>
              <p className="mt-2">
                <strong>Usage Data:</strong> We may collect certain information automatically when you visit our Site, 
                such as your IP address, browser type, referring/exit pages, operating system, 
                date/time stamps, and clickstream data.
              </p>
              <p className="mt-2">
                <strong>Your Images:</strong> CardCombiner processes your images entirely within your browser. 
                Your images are never sent to our servers or stored by us in any way.
              </p>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl md:text-2xl font-semibold mb-3 text-[#6B4EE6]">
                Cookies and Tracking Technologies
              </h2>
              <p>
                We use cookies and similar tracking technologies to collect information about your browsing 
                activities. Cookies are small text files stored on your device that help us provide and 
                improve our services.
              </p>
              <p className="mt-3">
                <strong>Types of cookies we use:</strong>
              </p>
              <ul className="list-disc ml-5 space-y-2 mt-2">
                <li>
                  <strong>Essential Cookies:</strong> Required for the basic functionality of our Site.
                </li>
                <li>
                  <strong>Analytics Cookies:</strong> Help us understand how visitors interact with our Site 
                  through services like Google Analytics.
                </li>
                <li>
                  <strong>Advertising Cookies:</strong> Used to deliver relevant advertisements and track 
                  ad campaign performance.
                </li>
              </ul>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl md:text-2xl font-semibold mb-3 text-[#6B4EE6]">
                Google AdSense and Advertising
              </h2>
              <p>
                We use Google AdSense to display advertisements on our Site. Google AdSense uses cookies 
                to serve ads based on your prior visits to our Site and other websites. Google's use of 
                advertising cookies enables it and its partners to serve ads based on your browsing history.
              </p>
              <p className="mt-3">
                <strong>How Google uses your data:</strong>
              </p>
              <ul className="list-disc ml-5 space-y-2 mt-2">
                <li>
                  Google may use cookies to personalize the ads you see based on your interests and 
                  browsing behavior.
                </li>
                <li>
                  Third-party vendors, including Google, use cookies to serve ads based on your prior 
                  visits to this website or other websites.
                </li>
                <li>
                  Google's advertising cookies (such as the DoubleClick cookie) enable Google and its 
                  partners to serve ads to you based on your visit to our Site and/or other sites on 
                  the Internet.
                </li>
              </ul>
              <p className="mt-3">
                <strong>Opting out of personalized advertising:</strong>
              </p>
              <p className="mt-2">
                You may opt out of personalized advertising by visiting{" "}
                <a 
                  href="https://www.google.com/settings/ads" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#6B4EE6] hover:underline"
                >
                  Google Ads Settings
                </a>
                . Alternatively, you can opt out of third-party vendor cookies for personalized 
                advertising by visiting{" "}
                <a 
                  href="https://www.aboutads.info/choices/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#6B4EE6] hover:underline"
                >
                  www.aboutads.info
                </a>
                .
              </p>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl md:text-2xl font-semibold mb-3 text-[#6B4EE6]">
                Google Analytics
              </h2>
              <p>
                We use Google Analytics to analyze how users interact with our Site. Google Analytics 
                collects information such as how often users visit the Site, what pages they visit, 
                and what other sites they used prior to coming to our Site.
              </p>
              <p className="mt-2">
                You can opt out of Google Analytics by installing the{" "}
                <a 
                  href="https://tools.google.com/dlpage/gaoptout" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#6B4EE6] hover:underline"
                >
                  Google Analytics Opt-out Browser Add-on
                </a>
                .
              </p>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl md:text-2xl font-semibold mb-3 text-[#6B4EE6]">
                How We Use Your Information
              </h2>
              <p>We may use the information we collect for various purposes, including to:</p>
              <ul className="list-disc ml-5 space-y-2 mt-2">
                <li>Provide, maintain, and improve our Site</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Analyze usage patterns and trends</li>
                <li>Display relevant advertisements through Google AdSense</li>
                <li>Protect against, identify, and prevent fraud and other illegal activity</li>
              </ul>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl md:text-2xl font-semibold mb-3 text-[#6B4EE6]">
                Your Cookie Choices
              </h2>
              <p>
                Most web browsers are set to accept cookies by default. You can usually choose to set 
                your browser to remove or reject cookies. Please note that if you choose to remove or 
                reject cookies, this could affect the availability and functionality of our Site.
              </p>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl md:text-2xl font-semibold mb-3 text-[#6B4EE6]">
                Third-Party Services
              </h2>
              <p>
                Our Site may contain links to third-party websites that are not operated by us. 
                We have no control over and assume no responsibility for the content, 
                privacy policies, or practices of any third-party sites or services.
              </p>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl md:text-2xl font-semibold mb-3 text-[#6B4EE6]">
                Contact Us
              </h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <p className="mt-2">
                Email: axel@lumbridgeholding.se
              </p>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Privacy;
